/**
 * src/auth/signOut.js — the app's one sign-out path (B-052).
 *
 * ⚠ JEST: importing this module pulls MSAL into the suite. It imports
 * msalInstance, which constructs MSAL's PublicClientApplication at import
 * time, and that constructor needs window.crypto, which jsdom lacks. So any
 * Jest suite that renders AppShell (which imports this module), or imports
 * this module some other way, must mock it, or it fails before a test runs:
 *   jest.mock('<relative path>/auth/signOut', () => ({ signOut: jest.fn() }));
 * Examples: src/shared/nav/__tests__/sections.lifecycleFieldInventory.test.js
 * and src/components/AppShell/__tests__/AppShell.accountPopover.test.js.
 * signOut.test.js mocks ./msalInstance instead, to test this module itself.
 *
 * A scoped full sign-out: MSAL's logoutRedirect for the signed-in account,
 * which clears that account from MSAL's cache and then sends the browser to
 * the CIAM end-session endpoint, so the server session ends too. Measured on
 * TEST 2026-11-04: the server session, not the tab, is what holds the
 * identity, so a local-only clear would sign straight back in as the same
 * account.
 *
 * WHAT IT DESTROYS, AND NOTHING ELSE:
 *   - MSAL's cache for this account (MSAL does this, inside logoutRedirect);
 *   - the two supplier sessionStorage key families, tmg_loading_session_* and
 *     tmg_rolloff_session_*. sessionStorage survives a same-tab navigation,
 *     so without this a second account signing in on this tab would resume
 *     the first account's in-progress load or roll-off.
 *
 * DELIBERATELY UNTOUCHED: every IndexedDB database (tmg-field,
 * tmg-disposal-queue, tmg-offline) and all of localStorage. Unsent queue
 * items are the only copy of some work, an unsent photo included, and
 * purgeFieldDB() would delete them. Do not add a purge here.
 *
 * Identity is resolved the way the rest of the app resolves it:
 * getAllAccounts()[0] (msalInstance.js, preflightToken.js, UserContext.jsx).
 */

import { msalInstance } from './msalInstance';
import { msalConfig } from '../authConfig';

const SUPPLIER_SESSION_PREFIXES = ['tmg_loading_session_', 'tmg_rolloff_session_'];

// Collect first, then remove: removing while walking key(i) shifts the
// indexes and skips the key after each removal. A storage error (a locked-down
// browser) is logged and never blocks the sign-out itself.
function clearSupplierSessions() {
  try {
    const doomed = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && SUPPLIER_SESSION_PREFIXES.some((p) => key.startsWith(p))) doomed.push(key);
    }
    doomed.forEach((key) => window.sessionStorage.removeItem(key));
  } catch (err) {
    console.warn('signOut: could not clear supplier session keys', err);
  }
}

/**
 * Sign the current account out. Resolves when the browser is navigating away;
 * rejects only when nothing was signed out, so the caller can say so.
 */
export async function signOut() {
  clearSupplierSessions();

  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    // Nothing for MSAL to end. A full page load still drops in-memory state.
    window.location.assign(window.location.origin);
    return;
  }

  const request = {
    account,
    postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
  };
  // login_hint is an optional claim; the tenant may not issue it. Omit the
  // field rather than pass undefined. (MSAL 3.30 would copy it from the
  // account itself when the field is absent; StandardInteractionClient.mjs.)
  const logoutHint = account.idTokenClaims?.login_hint;
  if (logoutHint) request.logoutHint = logoutHint;

  try {
    await msalInstance.logoutRedirect(request);
  } catch (err) {
    console.error('signOut: logoutRedirect failed', err);
    // MSAL clears the account from its cache BEFORE it reaches the server
    // (RedirectClient.mjs, logout). A failure after that point has already
    // signed this tab out, though the server session may survive: finish
    // with a full page load rather than leave a page whose account is gone.
    if (msalInstance.getAllAccounts().length === 0) {
      window.location.assign(window.location.origin);
      return;
    }
    throw err;
  }
}
